
import React from 'react';
import { ArrowRight, Zap, Shield, Eye, Cog, Database, BarChart3, Headphones, DollarSign, TrendingUp, Lock, FileText, CheckCircle, AlertCircle, RefreshCw, Users, Globe, Cpu, Cloud, CreditCard, Building, Gamepad2, Wallet } from 'lucide-react';
import Header from "./header";
import Footer from "./footer";
const IndustrySolutions = () => {
  // All Essential Reconciliation Cards
  const reconCards = [
    {
      id: 'lightning-processing',
      title: 'Lightning-Fast Processing',
      description: 'Process millions of transactions in minutes with our high-performance reconciliation engine. Handle peak volumes effortlessly.',
      icon: <Zap className="w-8 h-8 text-yellow-500" />,
      gradient: 'from-yellow-400 to-orange-500',
      features: ['Multi-threaded processing', 'Real-time reconciliation', 'Scalable architecture', 'Peak load handling']
    },
    {
      id: 'data-integration',
      title: 'Universal Data Integration',
      description: 'Connect with 170+ pre-built connectors. Seamlessly integrate data from any source, format, or system.',
      icon: <Database className="w-8 h-8 text-blue-500" />,
      gradient: 'from-blue-400 to-cyan-500',
      features: ['170+ connectors', 'Multiple data formats', 'API integrations', 'Real-time sync']
    },
    {
      id: 'ai-accuracy',
      title: 'AI-Powered Accuracy',
      description: 'Achieve 99.9% auto-reconciliation with AI-driven data extraction and intelligent matching algorithms.',
      icon: <Cpu className="w-8 h-8 text-purple-500" />,
      gradient: 'from-purple-400 to-pink-500',
      features: ['Machine learning', 'Pattern recognition', 'Smart matching', 'Continuous improvement']
    },
   
    {
      id: 'regulatory-compliance',
      title: 'Regulatory Compliance',
      description: 'Ensure strict adherence to regulatory requirements with comprehensive audit trails and validation checks.',
      icon: <Shield className="w-8 h-8 text-red-500" />,
      gradient: 'from-red-400 to-pink-500',
      features: ['Compliance checks', 'Audit trails', 'Regulatory reporting', 'Data security']
    },
    {
      id: 'scalable-cloud',
      title: 'Cloud-Native Architecture',
      description: 'Built for scale with cloud-native architecture that grows with your business needs.',
      icon: <Cloud className="w-8 h-8 text-sky-500" />,
      gradient: 'from-sky-400 to-blue-500',
      features: ['Auto-scaling', 'High availability', 'Global deployment', 'Disaster recovery']
    },
    {
      id: 'expert-support',
      title: 'Expert Support',
      description: 'Dedicated experts guide you through implementation and provide ongoing support for optimal performance.',
      icon: <Headphones className="w-8 h-8 text-orange-500" />,
      gradient: 'from-orange-400 to-red-500',
      features: ['24/7 support', 'Implementation guidance', 'Training programs', 'Best practices']
    },
    
    {
      id: 'advanced-analytics',
      title: 'Advanced Analytics',
      description: 'Deep insights with predictive analytics and trend analysis for proactive reconciliation management.',
      icon: <BarChart3 className="w-8 h-8 text-emerald-500" />,
      gradient: 'from-emerald-400 to-green-500',
      features: ['Predictive analytics', 'Trend analysis', 'KPI monitoring', 'Business intelligence']
    },
    {
      id: 'security-controls',
      title: 'Enterprise Security',
      description: 'Bank-grade security with encryption, access controls, and comprehensive data protection.',
      icon: <Lock className="w-8 h-8 text-slate-500" />,
      gradient: 'from-slate-400 to-gray-500',
      features: ['End-to-end encryption', 'Access controls', 'Data masking', 'Compliance certifications']
    },
    
  ];

  // Industry Sectors
  const sectors = [
    {
      id: 'banking',
      title: 'Banking & Financial Services',
      description: 'Comprehensive financial data reconciliation for banks, credit unions, and financial institutions.',
      icon: <Building className="w-10 h-10 text-blue-600" />,
      gradient: 'from-blue-600 to-purple-600',
      keyFeatures: ['Multi-currency support', 'Regulatory reporting', 'Real-time processing', 'Risk management'],
      useCases: ['Account reconciliation', 'Transaction matching', 'Nostro/Vostro reconciliation', 'Regulatory compliance']
    },
    {
      id: 'payments',
      title: 'Payment & Card Processing',
      description: 'Advanced payment reconciliation solutions for payment processors, acquirers, and card networks.',
      icon: <CreditCard className="w-10 h-10 text-green-600" />,
      gradient: 'from-green-600 to-teal-600',
      keyFeatures: ['High-volume processing', 'Multi-channel support', 'Real-time settlement', 'Fraud detection'],
      useCases: ['Card transaction reconciliation', 'Merchant settlement', 'Chargeback management', 'Fee reconciliation']
    },
    {
      id: 'fintech',
      title: 'Fintech & Lending',
      description: 'Next-generation reconciliation solutions for fintech companies and lending platforms.',
      icon: <TrendingUp className="w-10 h-10 text-purple-600" />,
      gradient: 'from-purple-600 to-pink-600',
      keyFeatures: ['API-first architecture', 'Real-time integration', 'Scalable processing', 'Automated workflows'],
      useCases: ['Loan reconciliation', 'Digital wallet matching', 'Investment tracking', 'Revenue recognition']
    },
    {
      id: 'gaming',
      title: 'Gaming Platform',
      description: 'Specialized reconciliation solutions for gaming platforms and entertainment companies.',
      icon: <Gamepad2 className="w-10 h-10 text-orange-600" />,
      gradient: 'from-orange-600 to-red-600',
      keyFeatures: ['Player account management', 'Multi-game support', 'Real-time processing', 'Payout automation'],
      useCases: ['Player balance reconciliation', 'Game transaction matching', 'Reward processing', 'Tournament settlements']
    },
    {
      id: 'ecommerce',
      title: 'E-commerce & Retail',
      description: 'Comprehensive reconciliation for online retailers and e-commerce platforms.',
      icon: <Wallet className="w-10 h-10 text-indigo-600" />,
      gradient: 'from-indigo-600 to-blue-600',
      keyFeatures: ['Multi-channel reconciliation', 'Inventory matching', 'Payment gateway integration', 'Returns processing'],
      useCases: ['Sales reconciliation', 'Inventory tracking', 'Refund processing', 'Commission calculations']
    },
    {
      id: 'insurance',
      title: 'Insurance & Benefits',
      description: 'Specialized solutions for insurance companies and employee benefits providers.',
      icon: <Shield className="w-10 h-10 text-emerald-600" />,
      gradient: 'from-emerald-600 to-green-600',
      keyFeatures: ['Claims processing', 'Premium reconciliation', 'Regulatory compliance', 'Multi-entity support'],
      useCases: ['Premium collection matching', 'Claims settlement', 'Reinsurance reconciliation', 'Commission tracking']
    }
  ];

  return (
    <div className="min-h-screen">
<Header/>
      {/* Hero Section - Dark Theme */}
      <div className="bg-gradient-to-r from-slate-900 via-gray-900 to-black text-white py-20">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
            Reconciliation Solutions
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Discover our comprehensive suite of reconciliation capabilities and industry-specific solutions designed to transform your financial operations
          </p>
        </div>
      </div>
      {/* Essential Reconciliation Cards Section - Light Theme */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6"> Reconciliation Solutions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful features that drive accuracy, efficiency, and compliance across all your reconciliation processes
            </p>
          </div> */}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {reconCards.map((card, index) => (
              <div key={card.id} className="group">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:transform hover:-translate-y-3 border border-gray-100 hover:border-gray-200 h-full">
                  <div className="flex flex-col h-full">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${card.gradient} text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      {card.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 group-hover:text-indigo-600 transition-colors">
                      {card.title}
                    </h3>
                    <div className="space-y-2">
                      {card.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-sm text-gray-500">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Divider Section - Dark Theme */}
      <div className="bg-gradient-to-r from-slate-800 via-gray-800 to-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            Tailored Solutions for Every Industry
          </h3>
          <p className="text-lg text-gray-300">
            Our reconciliation platform adapts to the unique needs of your industry with specialized features and workflows
          </p>
        </div>
      </div>

      {/* Industry Sectors Section - Light Theme */}
      <div className="bg-gradient-to-br from-white via-gray-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Industry-Specific Solutions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized reconciliation solutions designed for the unique challenges and requirements of your industry
            </p>
          </div> */}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sectors.map((sector, index) => {
              const cardStyles = [
                'hover:rotate-1 hover:scale-105',
                'hover:-rotate-1 hover:scale-105',
                'hover:rotate-0 hover:scale-110',
                'hover:rotate-1 hover:scale-105',
                'hover:-rotate-1 hover:scale-105',
                'hover:rotate-0 hover:scale-110'
              ];

              return (
                <div key={sector.id} className="group">
                  <div className={`bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:transform ${cardStyles[index % cardStyles.length]} border border-gray-100 hover:border-gray-200 h-full`}>
                    <div className="flex flex-col h-full">
                      <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r ${sector.gradient} text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                        {sector.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-indigo-600 transition-colors">
                        {sector.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-6 flex-grow">
                        {sector.description}
                      </p> 
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Call to Action - Dark Theme */}
      <div className="bg-gradient-to-r from-slate-900 via-gray-900 to-black text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
            Ready to Transform Your Reconciliation?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join thousands of companies that trust our comprehensive reconciliation solutions to streamline their financial operations and drive growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
             <a href="/Contact" className="flex items-center">
              <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105">
              Get Started Today
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
             </a>
           
            {/* <button className="border-2 border-gray-300 text-gray-300 px-8 py-4 rounded-full font-semibold hover:bg-gray-300 hover:text-gray-900 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
              Schedule a Demo
            </button> */}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default IndustrySolutions;